import React, { useRef, useEffect, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useRouter } from 'next/router';
import { t } from 'Src/i18n';
import type { OverloadForSpecificProposalIdsType } from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import { Notification } from 'Components/frameworks/notification/Notification';
import { Title } from 'Components/frameworks/title/Title';
import { ChildcareServiceBlock } from 'Components/widgets/childcare/ChildcareServiceBlock';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { TRACK_ACTION_CART_ADD, TRACK_ACTION_CART_REMOVE } from 'Tracking/events/cart.tracking';
import {
  getCtaTypeChildcare,
  getCurrentChild,
  getFormatDate,
  includedAndNotIncludedChildcares,
  getOverloadChildcareDataForChalet,
} from 'Components/widgets/childcare/childcare.helper';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { CHILDCARE_TRACKING, TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTracking } from 'Tracking/tracking-api/useTracking';
type Props = {
  addChildcare: (arg0: {
    childcareId: string;
    attendeeId: string;
    startDate: string;
    endDate: string;
    overloadChildcareIdForChalet?: OverloadForSpecificProposalIdsType;
  }) => Promise<any>;
  attendees: ReadonlyArray<{
    readonly id: string;
    readonly customerId?: string;
    readonly birthdate: string;
    readonly firstName?: string;
    readonly lastName?: string;
  }>;
  childcareAdditionalServices: ReadonlyArray<UpsellChildcare>;
  deleteChildcare: (arg0: {
    childcareId: string;
    attendeeId: string;
    startDate: string;
    endDate: string;
    overloadChildcareIdForChalet?: OverloadForSpecificProposalIdsType;
  }) => Promise<any>;
  isCartLocked?: boolean;
  isCondensed?: boolean;
  proposals?: ReadonlyArray<Proposal>;
  shouldTrack?: boolean;
};

const getBlockMinHeight = (wrapper: Node) => {
  if (!wrapper) {
    return 0;
  }

  const nodeHeightsList = [
    ...wrapper.querySelectorAll('[data-testid="ChildcareServiceBlock-childBirthdateMessage"]'),
  ].map((node) => node.clientHeight || 0);
  return Math.max(...nodeHeightsList);
};

const handleClick = ({
  proposals,
  childcare,
  addChildcare,
  deleteChildcare,
  trackClickEvent,
  ga3Track,
  isComparatorView,
}) => {
  // this triggers only when there is only one schedule and one slot
  // if looking for advanced cases check the contextchildcare
  const overloadChildcareIdForChalet =
    proposals &&
    getOverloadChildcareDataForChalet({
      proposals,
      currentChildcareId: childcare.pricingInfo.schedules[0].slots[0].id,
      currentChildId: childcare.pricingInfo.childId || '',
    });
  const currentAttendeeHasRentedChildcare =
    childcare.pricingInfo.attendees.find(({ id }) => id === childcare.pricingInfo?.childId)
      ?.rentedAmount?.value > 0;

  if (currentAttendeeHasRentedChildcare) {
    isComparatorView
      ? ga3Track({ ...TRACK_ACTION_CART_REMOVE, labelGA: CHILDCARE_TRACKING.REMOVE_CHILDCARE })
      : trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
          detail_click: CHILDCARE_TRACKING.REMOVE_CHILDCARE,
        });
    return deleteChildcare({
      childcareId: childcare.pricingInfo.progId || '',
      attendeeId: childcare.pricingInfo?.childId || '',
      startDate: childcare.pricingInfo.schedules[0].startDate,
      endDate: childcare.pricingInfo.schedules[0].startDate,
      overloadChildcareIdForChalet,
    });
  }

  isComparatorView
    ? ga3Track({ ...TRACK_ACTION_CART_ADD, labelGA: CHILDCARE_TRACKING.ADD_CHILDCARE })
    : trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
        detail_click: CHILDCARE_TRACKING.ADD_CHILDCARE,
      });
  // Those are regular childcares only one schedule and no slots
  return addChildcare({
    childcareId: childcare.pricingInfo.progId || '',
    attendeeId: childcare.pricingInfo?.childId || '',
    startDate: childcare.pricingInfo.schedules[0].startDate,
    endDate: childcare.pricingInfo.schedules[0].endDate,
    overloadChildcareIdForChalet,
  });
};

export const Childcare = ({
  addChildcare,
  attendees,
  childcareAdditionalServices,
  deleteChildcare,
  isCartLocked = false,
  isCondensed,
  proposals,
  shouldTrack = true,
}: Props) => {
  const { isDesktop } = useResponsive();
  const includedBlocks = useRef();
  const notIncludedBlocks = useRef();
  const [includedMinHeight, setIncludedMinHeight] = useState(0);
  const [notIncludedMinHeight, setNotIncludedMinHeight] = useState(0);
  const router = useRouter();
  const { trackClickEvent } = useTrackEvent();
  const ga3Track = useTracking();
  const isComparatorView = router.pathname === '/comparator';
  useEffect(() => {
    setIncludedMinHeight(getBlockMinHeight(includedBlocks.current));
    setNotIncludedMinHeight(getBlockMinHeight(notIncludedBlocks.current));
  });
  const { includedChildcares, notIncludedChildcares } = includedAndNotIncludedChildcares(
    childcareAdditionalServices,
  );

  if (attendees.length === 0) {
    return null;
  }

  const getChildBirthDateMessage = (childAttendeeId) => {
    if (!childAttendeeId) {
      return '';
    }

    const currentKid = getCurrentChild(attendees, childAttendeeId);

    if (currentKid && currentKid.firstName && currentKid.lastName) {
      return t('booking.services.childcare.firstnameLastnameBornOn', {
        lastName: currentKid.lastName,
        firstName: currentKid.firstName,
        birthdate: getFormatDate(currentKid, t('common.dateFormatTechnicalID')),
      });
    }

    return t('booking.services.childcare.forWhatKid', {
      birthdate: getFormatDate(currentKid, t('common.dateFormatTechnicalID')),
    });
  };

  return (
    <div data-testid="PackageChildcare">
      {childcareAdditionalServices.length === 0 ? (
        <Notification
          className="text-orange"
          text={t('booking.form.warnings.noChildcareMessage')}
        />
      ) : (
        <div>
          <Title className="mb-10 w-full md:whitespace-nowrap" theme="title-brand-n2">
            {t('booking.services.childcare.childcareTitle', {
              number: childcareAdditionalServices.length,
            })}
          </Title>
          {!!notIncludedChildcares.length && (
            <div
              className={classnames('-mx-10 flex flex-wrap', {
                'mb-20': !isDesktop,
                'mb-80': isDesktop,
              })}
              ref={notIncludedBlocks}
            >
              {notIncludedChildcares
                .sort(
                  (childcareEl: UpsellChildcare, nextChildcareEl: UpsellChildcare) =>
                    childcareEl.pricingInfo?.childId > nextChildcareEl.pricingInfo?.childId,
                )
                .map((childcare, index) => {
                  const { childId } = childcare.pricingInfo;
                  const childcareCta = getCtaTypeChildcare(childcare);
                  const shouldTriggerChildcareAdvancedReservationBlockOnClick =
                    childcare.pricingInfo.schedules?.length > 1;
                  const shouldTriggerChildcareMorningAfternoonExclusiveBlockOnClick =
                    childcare.pricingInfo.schedules?.length === 1 &&
                    childcare.pricingInfo.schedules[0].slots?.length >= 1 &&
                    childcare.pricingInfo.schedules[0].slots.length <= 3 &&
                    childcare.pricingInfo.schedules[0].slots.some(
                      ({ type }) => type === 'MORNING' || type === 'AFTERNOON',
                    );
                  // one schedule on slot ( the classic case )
                  const isSimpleChildcare =
                    !shouldTriggerChildcareAdvancedReservationBlockOnClick &&
                    !shouldTriggerChildcareMorningAfternoonExclusiveBlockOnClick;

                  // When cart is locked, force disabled type and skip click handlers
                  const effectiveType = isCartLocked ? 'disabled' : childcareCta.type;
                  const effectiveOnCtaClick = isCartLocked
                    ? undefined
                    : () => {
                        return handleClick({
                          proposals,
                          childcare,
                          addChildcare,
                          deleteChildcare,
                          trackClickEvent,
                          isComparatorView,
                          ga3Track,
                        });
                      };

                  return (
                    <ChildcareServiceBlock
                      blockMinHeight={notIncludedMinHeight}
                      childId={childId}
                      childBirthdateMessage={getChildBirthDateMessage(childId)}
                      childcare={childcare}
                      className={classnames('w-full p-10', {
                        'sm:w-1/2 md:w-1/3': !isCondensed,
                      })}
                      dataCsOverrideId={`Childcare-${index}`}
                      dataTestId="childcare-block"
                      isIncluded={false}
                      key={childId}
                      onCtaClick={effectiveOnCtaClick}
                      shouldTriggerChildcareAdvancedReservationBlockOnClick={
                        isCartLocked ? false : shouldTriggerChildcareAdvancedReservationBlockOnClick
                      }
                      shouldTriggerChildcareMorningAfternoonExclusiveBlockOnClick={
                        isCartLocked
                          ? false
                          : shouldTriggerChildcareMorningAfternoonExclusiveBlockOnClick
                      }
                      shouldTrack={shouldTrack && isSimpleChildcare}
                      trackingType={childcareCta.tracking}
                      type={effectiveType}
                    />
                  );
                })}
            </div>
          )}
          {!!includedChildcares.length && (
            <div className="-mx-10 flex flex-wrap" ref={includedBlocks}>
              {includedChildcares.map((childcare, index) => (
                <ChildcareServiceBlock
                  blockMinHeight={includedMinHeight}
                  childBirthdateMessage={getChildBirthDateMessage(childcare.pricingInfo.childId)}
                  childcare={childcare}
                  childId={childcare.pricingInfo.childId}
                  className={classnames('w-full p-10', {
                    'sm:w-1/2 md:w-1/3': !isCondensed,
                  })}
                  dataCsOverrideId={`Childcare-${index}`}
                  dataTestId="childcare-block"
                  isIncluded
                  key={childcare.pricingInfo.childId}
                  shouldTrack={shouldTrack}
                  type="disabled"
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
